import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

/* ================= Helpers ================= */
const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const fecha = new Date(fechaStr);
  if (Number.isNaN(fecha.getTime())) return "—";
  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, "0");
  const d = String(fecha.getDate()).padStart(2, "0");
  return `${d}/${m}/${y}`;
};

/* ================= Component ================= */
const ListarActividad = ({ refresh }) => {
  const [actividades, setActividades] = useState([]); // ✅ SOLO DATA (sin JSX)
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();
  const { id } = useParams();

  /* ================= Eliminar ================= */
  const deleteActividad = useCallback(async (actividadId) => {
    try {
      const result = await Swal.fire({
        title: "¿Está seguro?",
        text: "¡No podrá revertir este proceso!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      setDeletingId(actividadId);

      const { data } = await axios.delete(`/api/actividad/delete/${actividadId}`);

      // ✅ actualización optimista (solo data)
      setActividades((prev) => prev.filter((a) => a._id !== actividadId));

      await Swal.fire({
        icon: "success",
        title: data?.message || "Actividad eliminada",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Error al eliminar la Actividad:", error.message);
      Swal.fire("Error", "No se pudo eliminar la actividad.", "error");
    } finally {
      setDeletingId(null);
    }
  }, []);

  /* ================= Obtener datos ================= */
  const getData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const { data } = await axios.get(`/api/actividad/listbyid/${id}`);

      // OJO: según tu backend puede venir {ok, actividades} en vez de array
      const lista = Array.isArray(data) ? data : (data?.actividades || []);

      const formateadas = (lista || [])
        .map((actividad) => ({
          ...actividad,
          fecha: formatearFecha(actividad.fecha),
        }))
        .reverse();

      setActividades(formateadas);
    } catch (error) {
      console.error("Error al cargar las actividades:", error.message);
      Swal.fire("Error", "No se pudo cargar la lista de actividades.", "error");
      setActividades([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  /* ================= Effects ================= */
  useEffect(() => {
    getData();
  }, [getData, refresh]);

  /* ================= Rows con funciones (JSX) ================= */
  const actividadesConFunciones = useMemo(() => {
    return (actividades || []).map((actividad) => ({
      ...actividad,
      funciones: (
        <div className="d-flex gap-2 flex-nowrap justify-content-end align-items-center">
          {/* Asistencia */}
          <button
            className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34 }}
            onClick={() => navigate(`/asistencia/${actividad._id}`)}
            title="Tomar asistencia"
            type="button"
            disabled={loading || deletingId === actividad._id}
          >
            <i className="fa-solid fa-list-check"></i>
          </button>

          {/* Eliminar */}
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34 }}
            onClick={() => deleteActividad(actividad._id)}
            title="Eliminar actividad"
            type="button"
            disabled={loading || deletingId === actividad._id}
          >
            {deletingId === actividad._id ? (
              <i className="fa-solid fa-spinner fa-spin text-white"></i>
            ) : (
              <i className="fa-solid fa-trash"></i>
            )}
          </button>
        </div>
      ),
    }));
  }, [actividades, deleteActividad, deletingId, loading, navigate]);

  /* ================= Columns ================= */
  const columns = useMemo(
    () => [
      { Header: "Nombre", accessor: "nombreActividad" },
      { Header: "Descripción", accessor: "descripcion" },
      { Header: "Fecha", accessor: "fecha" },
      { accessor: "funciones" },
    ],
    []
  );

  /* ================= Render ================= */
  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Lista de Actividades</h5>
                <small className="text-secondary">
                  Gestión de actividades: asistencia y eliminación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"} me-2`}></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate(`/actividad/nuevo/${id}`)}
                  disabled={loading}
                  title="Registrar Acta"
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando actividades…</div>
              </div>
            ) : Array.isArray(actividadesConFunciones) && actividadesConFunciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={actividadesConFunciones} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🗂️</div>
                <div className="fw-semibold">No hay actividades registradas</div>
                <div className="small">No se encontraron actividades para esta matrícula.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarActividad;
